import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface AdBannerProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle';
  responsive?: 'true' | 'false';
}

const AdBanner: React.FC<AdBannerProps> = ({ slot, format = 'auto', responsive = 'false' }) => {
  const { pathname } = useLocation();
  const adRef = useRef<HTMLModElement>(null);

  useEffect(() => {
    // すでに処理済み（Googleが属性を付与済み）なら何もしない
    if (adRef.current?.getAttribute('data-adsbygoogle-status')) {
      return;
    }

    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 5;

    const loadAd = () => {
      if (!isMounted) return;
      
      const adElement = adRef.current;
      if (!adElement) {
        console.log(`[AdSense] スロット ${slot}: 広告要素(ref)が見つかりません。`);
        return;
      }

      const availableWidth = adElement.offsetWidth;
      console.log(`[AdSense] スロット ${slot}: 検知された幅 = ${availableWidth}px`);

      // 幅が0の場合は、まだレンダリングが終わっていない可能性があるためリトライする
      if (availableWidth === 0) {
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`[AdSense] スロット ${slot}: 幅が0のためリトライします (${retryCount}/${maxRetries})`);
          setTimeout(loadAd, 500); // 500ms後にリトライ
        }
        return;
      }

      try {
        const adsbygoogle = (window as any).adsbygoogle;
        
        // 広告の状態と子要素（iframe）の有無を確認
        const status = adElement.getAttribute('data-adsbygoogle-status');
        const isLoaded = adElement.children.length > 0;

        console.log(`[AdSense] スロット ${slot}: 状態 = ${status || '未初期化'}, 子要素あり = ${isLoaded}`);

        if (adsbygoogle && !status && !isLoaded) {
          console.log(`[AdSense] スロット ${slot}: adsbygoogle.push({}) を実行します`);
          adsbygoogle.push({});
        }
      } catch (e) {
        console.error('AdSense error:', e);
      }
    };

    // 初回実行
    const timer = setTimeout(loadAd, 1000);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [pathname, slot]); 

  return (
    <div 
      key={pathname} 
      className="ad-container"
      style={{ 
        width: '100%', 
        minHeight: '100px', 
        textAlign: 'center', 
        margin: '20px 0', 
        position: 'relative' 
      }}
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        // display: block だけでなく、明確な高さを確保し、アドセンス側での自動計算ミスを防ぐ
        // data-ad-format を "auto" から、より制限の強い "horizontal" や "rectangle" に変えるのも有効です
        style={{ display: 'block', minWidth: '250px', minHeight: '100px', width: '100%' }}
        data-ad-client="ca-pub-1656424757905016"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive}
      />
    </div>
  );
};

export default AdBanner;